import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ClipboardCheck, Loader2, Plus, Trash2, Save, Route as RouteIcon, FileSignature } from "lucide-react";

export default function SopralluoghiSettings() {
  const [tiers, setTiers] = useState([]);
  const [distTiers, setDistTiers] = useState([]);
  const [condizioni, setCondizioni] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/settings/sopralluogo").then((r) => {
      setTiers(r.data.tiers || []);
      setDistTiers(r.data.distanza_tiers || []);
      setCondizioni(r.data.condizioni || "");
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setRow = (i, k, v) => setTiers((t) => t.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const addRow = () => setTiers((t) => [...t, { fino_a_kw: "", quota_fissa: "" }]);
  const removeRow = (i) => setTiers((t) => t.filter((_, idx) => idx !== i));

  const setDistRow = (i, k, v) => setDistTiers((t) => t.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const addDistRow = () => setDistTiers((t) => [...t, { fino_a_km: "", eur_km: "" }]);
  const removeDistRow = (i) => setDistTiers((t) => t.filter((_, idx) => idx !== i));

  const save = async () => {
    const payload = {
      tiers: tiers.map((t) => ({
        fino_a_kw: Number(t.fino_a_kw),
        quota_fissa: Number(t.quota_fissa || 0),
      })),
      distanza_tiers: distTiers.map((t) => ({
        fino_a_km: Number(t.fino_a_km),
        eur_km: Number(t.eur_km || 0),
      })),
      condizioni,
    };
    if (payload.tiers.some((t) => Number.isNaN(t.fino_a_kw) || Number.isNaN(t.quota_fissa))) {
      toast.error("Compila 'Fino a kW' e 'Quota fissa' per ogni fascia di potenza");
      return;
    }
    if (payload.distanza_tiers.some((t) => Number.isNaN(t.fino_a_km) || Number.isNaN(t.eur_km))) {
      toast.error("Compila 'Fino a km' e '€/km' per ogni fascia di distanza");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/sopralluogo", payload);
      setTiers(data.tiers);
      setDistTiers(data.distanza_tiers || []);
      setCondizioni(data.condizioni || "");
      toast.success("Configurazione sopralluoghi salvata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-primary" strokeWidth={1.5} /> Sopralluoghi</h1>
          <p className="text-xs text-muted-foreground">Costo trasporto €/km + quota fissa per fascia di impianto e condizioni da firmare</p>
        </div>
        <Button onClick={save} disabled={saving || loading} data-testid="save-sopralluogo-btn" className="font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva
        </Button>
      </header>

      <div className="p-4 lg:p-8 space-y-6">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <>
            <Card className="rounded border-border p-5" data-testid="sopralluogo-tiers-card">
              <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold mb-4 flex items-center gap-2">
                <RouteIcon className="h-4 w-4 text-primary" strokeWidth={1.5} /> Quota fissa per fascia di potenza
              </h2>
              <p className="text-xs text-muted-foreground mb-5">
                La <b>quota fissa</b> del sopralluogo dipende dalla potenza dell'impianto. Si sceglie la prima fascia il cui limite <b>"Fino a kW" ≥</b> potenza impianto. Il costo <b>€/km</b> è invece definito nella tabella "Fasce di distanza" qui sotto.
              </p>

              <div className="hidden sm:grid grid-cols-[200px_200px_40px] gap-3 px-1 pb-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                <div>Fino a (kW)</div><div>Quota fissa (€)</div><div></div>
              </div>

              <div className="space-y-2">
                {tiers.map((t, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-[200px_200px_40px] gap-3 items-center" data-testid={`sopralluogo-row-${i}`}>
                    <div className="space-y-1 sm:space-y-0">
                      <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">Fino a (kW)</Label>
                      <Input type="number" value={t.fino_a_kw} onChange={(e) => setRow(i, "fino_a_kw", e.target.value)} data-testid={`sopralluogo-kw-${i}`} placeholder="100" />
                    </div>
                    <div className="space-y-1 sm:space-y-0">
                      <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">Quota fissa (€)</Label>
                      <Input type="number" value={t.quota_fissa} onChange={(e) => setRow(i, "quota_fissa", e.target.value)} data-testid={`sopralluogo-quota-${i}`} placeholder="200" />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeRow(i)} data-testid={`sopralluogo-remove-${i}`} className="text-destructive hover:text-destructive justify-self-end col-span-2 sm:col-span-1">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button variant="outline" size="sm" onClick={addRow} data-testid="add-sopralluogo-btn" className="mt-4">
                <Plus className="h-4 w-4 mr-1.5" /> Aggiungi fascia
              </Button>
            </Card>

            <Card className="rounded border-border p-5" data-testid="sopralluogo-dist-tiers-card">
              <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold mb-4 flex items-center gap-2">
                <RouteIcon className="h-4 w-4 text-primary" strokeWidth={1.5} /> €/km per fascia di distanza
              </h2>
              <p className="text-xs text-muted-foreground mb-5">
                Il costo <b>€/km</b> del trasporto dipende dalla <b>distanza andata+ritorno dalla sede più vicina</b> tra TORRI e le agenzie abilitate. Si sceglie la prima fascia il cui limite <b>"Fino a km" ≥</b> distanza a/r. Trasporto = €/km della fascia × distanza a/r.
              </p>

              <div className="hidden sm:grid grid-cols-[200px_200px_40px] gap-3 px-1 pb-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                <div>Fino a (km)</div><div>€/km</div><div></div>
              </div>

              <div className="space-y-2">
                {distTiers.map((t, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-[200px_200px_40px] gap-3 items-center" data-testid={`sopralluogo-dist-row-${i}`}>
                    <div className="space-y-1 sm:space-y-0">
                      <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">Fino a (km)</Label>
                      <Input type="number" value={t.fino_a_km} onChange={(e) => setDistRow(i, "fino_a_km", e.target.value)} data-testid={`sopralluogo-dist-km-${i}`} placeholder="50" />
                    </div>
                    <div className="space-y-1 sm:space-y-0">
                      <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">€/km</Label>
                      <Input type="number" value={t.eur_km} onChange={(e) => setDistRow(i, "eur_km", e.target.value)} data-testid={`sopralluogo-dist-eurkm-${i}`} placeholder="1" />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeDistRow(i)} data-testid={`sopralluogo-dist-remove-${i}`} className="text-destructive hover:text-destructive justify-self-end col-span-2 sm:col-span-1">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button variant="outline" size="sm" onClick={addDistRow} data-testid="add-sopralluogo-dist-btn" className="mt-4">
                <Plus className="h-4 w-4 mr-1.5" /> Aggiungi fascia di distanza
              </Button>
            </Card>

            <Card className="rounded border-border p-5" data-testid="sopralluogo-condizioni-card">
              <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold mb-4 flex items-center gap-2">
                <FileSignature className="h-4 w-4 text-primary" strokeWidth={1.5} /> Condizioni da firmare
              </h2>
              <p className="text-xs text-muted-foreground mb-3">Testo mostrato al cliente in Outlook e nel documento del sopralluogo, che deve essere accettato/firmato prima del pagamento.</p>
              <Textarea value={condizioni} onChange={(e) => setCondizioni(e.target.value)} rows={8} data-testid="sopralluogo-condizioni-text" placeholder="Testo delle condizioni del sopralluogo…" />
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
